import { ChevronDown, ChevronRight, X } from 'lucide-react';
import { useId, useState } from 'react';
import type {
  ChatActivityIcon,
  ChatActivityItem,
  ChatActivityLink,
} from '@wundo/shared/chat.ts';
import { cn } from '../../lib/cn.ts';
import { useTruncated } from '../../lib/useTruncated.ts';
import { ChatActivityIconGlyph } from './chat-activity-icon.tsx';
import { ChatCardLink } from './chat-card.tsx';
import { chatActivityItemKey } from './chatActivityItemKey.ts';

export type ChatActivityStepState = 'running' | 'done' | 'failed';

export interface ChatActivityStep {
  id: string;
  /** What the step does or did, in plain words, e.g. "Ik bekijk je agenda". */
  text: string;
  state: ChatActivityStepState;
  icon?: ChatActivityIcon;
  /** What the step touched, one line each when it is unfolded, e.g. the memories it read. */
  detail?: readonly ChatActivityItem[];
  /** Words in `text` that open the page of what the step is about. */
  link?: ChatActivityLink;
}

export interface ChatActivityProps {
  steps: readonly ChatActivityStep[];
  /** Said after a step that did not work, e.g. "lukte niet". Left out, a cross says it. */
  failedLabel?: string;
  /** Names the unfold button of a step whose words hold a link, e.g. "Toon details". */
  detailsLabel?: string;
  /**
   * Opens one of a step's items in full, e.g. in a panel over the chat. Left
   * out, the items are only listed.
   */
  onOpenItem?: (step: ChatActivityStep, index: number) => void;
  className?: string;
}

const ROW: Record<ChatActivityStepState, string> = {
  running: 'text-ink-secondary',
  done: 'text-ink-secondary',
  failed: 'text-ink-muted',
};

const NODE: Record<ChatActivityStepState, string> = {
  running:
    'animate-step-ring border-transparent bg-surface text-ink motion-reduce:animate-none',
  done: 'border-success/22 bg-accent/10 text-success',
  failed: 'border-dashed border-line bg-fill-faint text-ink-faint',
};

/*
 * Light passes over the words of the step Wundo is on. Without motion they
 * simply stand in full ink, so the running step still reads as the current one.
 */
/*
 * Each step draws the line down to the next node, through whatever it unfolds,
 * so a single step stands alone and an open one stays joined to the next.
 */
const CONNECTOR =
  'not-last:before:absolute not-last:before:top-4.5 not-last:before:-bottom-5 not-last:before:left-3.25 not-last:before:w-[1.5px] not-last:before:rounded-full not-last:before:bg-line';

const FOCUS =
  'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink-faint';

const RUNNING_LABEL =
  'animate-shimmer bg-linear-to-r from-ink-muted from-35% via-ink via-50% to-ink-muted to-65% bg-size-[200%_100%] bg-clip-text font-medium text-transparent motion-reduce:animate-none motion-reduce:bg-none motion-reduce:text-ink';

/**
 * What Wundo does while it works on a reply, as a timeline: one node per step
 * on a line, in the order the steps happened. A row appears the moment its
 * step starts and changes in place when it ends, so nothing jumps; lined up
 * with the bubble rather than the avatar, so it reads as part of the reply.
 */
export function ChatActivity({
  steps,
  failedLabel,
  detailsLabel = 'Details',
  onOpenItem,
  className,
}: ChatActivityProps) {
  if (steps.length === 0) {
    return null;
  }

  return (
    <ul
      data-slot='chat-activity'
      className={cn('relative -mb-1 ml-9 flex flex-col gap-0.5', className)}
    >
      {steps.map((step) => (
        <ActivityRow
          key={step.id}
          step={step}
          failedLabel={failedLabel}
          detailsLabel={detailsLabel}
          onOpenItem={onOpenItem}
        />
      ))}
    </ul>
  );
}

/*
 * One line, always: a step that says more than fits, or found things worth
 * naming, unfolds below itself. Its words are the button, unless they hold a
 * link — a link inside a button is neither — and then the chevron is.
 */
function ActivityRow({
  step,
  failedLabel,
  detailsLabel,
  onOpenItem,
}: {
  step: ChatActivityStep;
  failedLabel?: string;
  detailsLabel: string;
  onOpenItem?: (step: ChatActivityStep, index: number) => void;
}) {
  const [open, setOpen] = useState(false);
  const [label, setLabel] = useState<HTMLSpanElement | null>(null);
  const panelId = useId();
  const running = step.state === 'running';
  const words = running ? `${step.text}…` : step.text;
  const truncated = useTruncated(label, words);
  const detail = step.detail ?? [];
  const unfolds = truncated || detail.length > 0;
  const toggle = {
    type: 'button' as const,
    'aria-expanded': open,
    'aria-controls': panelId,
    onClick: () => setOpen((current) => !current),
  };
  const chevron = (
    <ChevronDown
      size={14}
      aria-hidden='true'
      className={cn('shrink-0 text-ink-faint transition-transform', open && 'rotate-180')}
    />
  );
  const text = (
    <span
      ref={setLabel}
      data-slot='chat-activity-text'
      className={cn('min-w-0 truncate', running && RUNNING_LABEL)}
    >
      <StepWords text={words} link={step.link} />
    </span>
  );

  return (
    <li
      data-slot='chat-activity-step'
      data-state={step.state}
      className={cn('relative flex flex-col py-1 text-base', CONNECTOR, ROW[step.state])}
    >
      <div className='flex min-h-7 items-center gap-3'>
        <span
          className={cn(
            'relative z-1 flex size-7 shrink-0 items-center justify-center rounded-full border',
            NODE[step.state],
          )}
        >
          {running ? (
            <span
              data-slot='chat-activity-spinner'
              aria-hidden='true'
              className='absolute -inset-px animate-spin rounded-full border-2 border-fill border-t-primary motion-reduce:animate-none'
            />
          ) : null}
          <ChatActivityIconGlyph icon={step.icon} />
        </span>
        {unfolds && !step.link ? (
          <button
            {...toggle}
            className={cn(
              'flex min-w-0 cursor-pointer items-center gap-1.5 rounded-sm text-left hover:text-ink',
              FOCUS,
            )}
          >
            {text}
            {chevron}
          </button>
        ) : (
          <span className='flex min-w-0 items-center gap-1.5'>
            {text}
            {unfolds ? (
              <button
                {...toggle}
                aria-label={detailsLabel}
                className={cn('flex shrink-0 cursor-pointer rounded-sm p-0.5', FOCUS)}
              >
                {chevron}
              </button>
            ) : null}
          </span>
        )}
        {step.state === 'failed' ? (
          failedLabel ? (
            <span className='-ml-1 shrink-0 text-sm text-danger'>· {failedLabel}</span>
          ) : (
            <X size={12} aria-hidden='true' className='-ml-1 shrink-0 text-danger' />
          )
        ) : null}
      </div>
      {open && unfolds ? (
        <div
          id={panelId}
          data-slot='chat-activity-detail'
          className='flex flex-col gap-1 pt-1.5 pb-2 pl-10 text-sm text-ink-muted'
        >
          {truncated ? (
            <p className='break-words text-ink-secondary'>
              <StepWords text={words} link={step.link} small />
            </p>
          ) : null}
          {detail.length > 0 ? (
            <ul data-slot='chat-activity-items' className='-ml-2 flex flex-col'>
              {detail.map((item, index) => (
                <li key={index} className='flex'>
                  <ItemLine
                    item={item}
                    itemKey={chatActivityItemKey(step.id, index)}
                    icon={step.icon === 'search' ? 'document' : step.icon}
                    onOpen={onOpenItem ? () => onOpenItem(step, index) : undefined}
                  />
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      ) : null}
    </li>
  );
}

/*
 * One thing the step touched, on one line however long it is: the full text
 * opens elsewhere, so the timeline stays short.
 */
function ItemLine({
  item,
  itemKey,
  icon,
  onOpen,
}: {
  item: ChatActivityItem;
  itemKey: string;
  icon?: ChatActivityIcon;
  onOpen?: () => void;
}) {
  const content = (
    <>
      <span className='flex text-ink-muted'>
        <ChatActivityIconGlyph icon={icon} />
      </span>
      <span className='min-w-0 flex-1 truncate'>{item.title}</span>
    </>
  );
  const line =
    'flex w-full min-w-0 items-center gap-2.25 rounded-sm px-2 py-1.25 text-base text-ink-body';

  if (!onOpen) {
    return <span className={line}>{content}</span>;
  }

  return (
    <button
      type='button'
      data-slot='chat-activity-item'
      data-activity-item={itemKey}
      onClick={onOpen}
      className={cn(
        'group/item cursor-pointer text-left hover:bg-fill hover:text-ink',
        line,
        FOCUS,
      )}
    >
      {content}
      <ChevronRight
        size={13}
        aria-hidden='true'
        className='shrink-0 text-ink-muted opacity-0 transition-opacity group-hover/item:opacity-100 group-focus-visible/item:opacity-100'
      />
    </button>
  );
}

/** The step's words, with the name in them that opens its page. */
function StepWords({
  text,
  link,
  small = false,
}: {
  text: string;
  link?: ChatActivityLink;
  small?: boolean;
}) {
  const at = link ? text.indexOf(link.text) : -1;
  if (!link || at === -1) {
    return text;
  }

  return (
    <>
      {text.slice(0, at)}
      <ChatCardLink
        href={link.url}
        className={cn('font-medium hover:text-ink', small && 'text-sm')}
      >
        {link.text}
      </ChatCardLink>
      {text.slice(at + link.text.length)}
    </>
  );
}
